import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { InfoIcon } from 'lucide-react';
import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { SettledContent, SETTLED_STATE } from '@/shared/ui/animations';
import { EmptyStateTitle } from '@/shared/ui';
import {
  PROJECT_CAMPAIGNS_PAGE_SIZE,
  projectCampaignsQueryOptions,
  projectPerformanceQueryOptions,
} from '@/entities/media-campaign';
import { usePerformanceFilters } from '@/features/performance-filters';
import { ScopeBreadcrumb, useScope } from '@/features/performance-scope';
import { BreakdownTable } from '@/widgets/performance-dashboard';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import { ROUTES } from '@/shared/config/routes';

/**
 * Performance → Creative.
 *
 * Ranks creatives by the same `BreakdownTable` Overview uses, scoped by the same date
 * range, platform/channel filters and campaign drill-down. Three states over the response
 * are kept apart deliberately, because they are three different statements:
 *
 * - No rows, and every campaign in scope is in `campaignsWithoutBreakdown` - none of the
 *   connected platforms reports delivery per creative. This is a platform-capability
 *   statement, not a claim that any campaign delivered nothing.
 * - Rows present, but some campaigns are in `campaignsWithoutBreakdown` - the leaderboard
 *   is real but partial, so the missing campaigns are named rather than silently absent
 *   from a ranking a marketer might otherwise trust as complete.
 * - Rows present, none missing - a complete leaderboard, no caveat.
 *
 * A fourth state, `campaignCount === 0`, is not a grain question at all: the date range,
 * platform/channel filters or campaign scope excluded every campaign before creative
 * reporting even comes into it, so it reads as "nothing delivered in this view" instead.
 */
export function Creative() {
  const { t } = useTranslation('projects');
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const projectId = project.platformProjectId;
  const { filters } = usePerformanceFilters(projectId);
  const { scope, setScope } = useScope(projectId);

  const current = useQuery(
    projectPerformanceQueryOptions(projectId, {
      startDate: filters.range.startDate,
      endDate: filters.range.endDate,
      dimension: 'creative',
      platforms: filters.platforms,
      channels: filters.channels,
      campaignId: scope.campaignId ?? undefined,
    }),
  );

  // Same reasons as Overview: the breakdown is keyed by creative, not by campaign, so it
  // can neither name the scope nor prove a campaign is absent from the project.
  const campaigns = useQuery(projectCampaignsQueryOptions(projectId));
  const projectCampaigns = campaigns.data;

  const campaignName = useMemo(
    () =>
      scope.campaignId
        ? projectCampaigns?.find((campaign) => campaign.id === scope.campaignId)?.name
        : undefined,
    [scope.campaignId, projectCampaigns],
  );

  const scopeIsAbsent =
    scope.campaignId !== null &&
    projectCampaigns !== undefined &&
    campaignName === undefined &&
    projectCampaigns.length < PROJECT_CAMPAIGNS_PAGE_SIZE;

  const rows = current.data?.breakdown ?? [];
  const campaignsWithoutBreakdown = current.data?.campaignsWithoutBreakdown ?? [];

  const missingCampaignNames = useMemo(
    () =>
      campaignsWithoutBreakdown.map(
        (id) => projectCampaigns?.find((campaign) => campaign.id === id)?.name ?? id,
      ),
    [campaignsWithoutBreakdown, projectCampaigns],
  );

  const isPartial = rows.length > 0 && campaignsWithoutBreakdown.length > 0;

  // One crossfade for the whole answer, so the breakdown and the invitation to
  // add a campaign never dissolve into an identical copy of themselves.
  const viewState = current.isError
    ? SETTLED_STATE.error
    : projectCampaigns?.length === 0 ||
        scopeIsAbsent ||
        (!current.isLoading && (current.data?.campaignCount === 0 || rows.length === 0))
      ? SETTLED_STATE.empty
      : SETTLED_STATE.rows;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4" data-testid="performance-creative-view">
      <ScopeBreadcrumb scope={scope} campaignName={campaignName} onWiden={setScope} />

      <SettledContent
        state={viewState}
        className="min-h-0 flex-1"
        contentClassName="flex min-h-0 flex-1 flex-col gap-4"
      >
        {current.isError ? (
          <Empty role="alert" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceCreative.couldNotLoadCreativePerformance')}
              </EmptyStateTitle>
              <EmptyDescription>
                {current.error instanceof Error
                  ? current.error.message
                  : t('projectDetail.performanceCreative.theRequestDidNotComeBack')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={() => void current.refetch()}>
                {t('projectDetail.performanceCreative.tryAgain')}
              </Button>
            </EmptyContent>
          </Empty>
        ) : projectCampaigns?.length === 0 ? (
          <Empty role="status" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceCreative.noCampaignsYet')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performanceCreative.thisLeaderboardRanksTheCampaigns')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" asChild>
                <Link to={ROUTES.PROJECT_PERFORMANCE_CAMPAIGNS(projectId)}>
                  {t('projectDetail.performanceCreative.addACampaign')}
                </Link>
              </Button>
            </EmptyContent>
          </Empty>
        ) : scopeIsAbsent ? (
          <Empty role="status" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceCreative.thisCampaignIsntInThisProject')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performanceCreative.theCampaignThisLinkPointsTo')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : !current.isLoading && current.data?.campaignCount === 0 ? (
          <Empty role="status" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceCreative.nothingDeliveredInThisView')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performanceCreative.noCampaignMatchesTheseFilters')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : !current.isLoading && rows.length === 0 ? (
          // Reached only once the project has campaigns, the scope names one of them (or
          // none), and the filters left at least one campaign in scope - so the absence of
          // rows is the platforms' doing, not the filters' or the link's.
          <Empty className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceCreative.creativeReportingIsNotAvailable')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performanceCreative.noneOfTheConnectedAccounts')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <>
            <BreakdownTable rows={rows} dimension="creative" isLoading={current.isLoading} />
            {/* After the table, not before it: the ranking is what the reader came for, and a
              caveat above it takes the attention the data should have. Still on the page,
              because a leaderboard missing half the spend is worth knowing about. */}
            {isPartial && (
              <p
                className="flex items-start gap-2 text-sm text-muted-foreground"
                data-testid="partial-coverage-note"
              >
                <InfoIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  {t('projectDetail.performanceCreative.missingFromThisLeaderboard', {
                    campaignCount: campaignsWithoutBreakdown.length,
                    names: missingCampaignNames.join(', '),
                  })}
                </span>
              </p>
            )}
          </>
        )}
      </SettledContent>
    </div>
  );
}
